'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

/* ─── DATA ───────────────────────────────────────────── */

const groceryCategories = [
  { icon: 'SparklesIcon', label: 'Fruits & Vegetables' },
  { icon: 'CubeIcon', label: 'Dairy & Eggs' },
  { icon: 'FireIcon', label: 'Bakery & Bread' },
  { icon: 'ShoppingCartIcon', label: 'Meat & Seafood' },
  { icon: 'WrenchIcon', label: 'Beverages' },
  { icon: 'TagIcon', label: 'Snacks & Packaged' },
  { icon: 'StarIcon', label: 'Personal Care' },
  { icon: 'HomeModernIcon', label: 'Household & Cleaning' },
  { icon: 'HeartIcon', label: 'Health & Nutrition' },
  { icon: 'CubeIcon', label: 'Frozen Foods' },
  { icon: 'DocumentTextIcon', label: 'Staples & Grains' },
  { icon: 'PlusCircleIcon', label: 'Custom Categories' },
];

const coreFeatures = [
  {
    num: '01',
    icon: 'CubeIcon',
    label: 'Real-time Inventory & Stock Management',
    desc: 'Every product update — stock addition, sale, return, or write-off — is reflected in real time across your store dashboard and customer app. Low-stock alerts trigger automatic reorder prompts before items run out.',
    pills: ['Live stock levels', 'Low-stock alerts', 'Auto-reorder triggers', 'Expiry date tracking', 'Bulk import/export'],
  },
  {
    num: '02',
    icon: 'CalendarDaysIcon',
    label: 'Slot-based Delivery Scheduling',
    desc: 'Customers choose their preferred delivery window at checkout — express (1–2 hours), same-day morning, afternoon, or evening slots, or next-day scheduled. Slot capacity is capped and managed automatically to prevent overload.',
    pills: ['Express delivery', 'Same-day slots', 'Next-day booking', 'Slot capacity control', 'Rescheduling support'],
  },
  {
    num: '03',
    icon: 'DevicePhoneMobileIcon',
    label: 'Customer App with Smart Search & Filters',
    desc: 'Customers search by product name, brand, or category — with smart suggestions and voice search. Filters for diet type, price range, brand, and ratings make it easy to find exactly what they need in seconds.',
    pills: ['Voice search', 'Diet & brand filters', 'Barcode scanner', 'Recently bought', 'Wishlist & save'],
  },
  {
    num: '04',
    icon: 'TruckIcon',
    label: 'Last-mile Delivery Optimization',
    desc: "The dispatch engine auto-assigns delivery partners based on proximity, vehicle capacity, and current load. Route optimization batches nearby deliveries to reduce distance driven and cut per-order delivery cost.",
    pills: ['AI route batching', 'Vehicle-load matching', 'Real-time ETAs', 'Live driver tracking', 'Delivery proof'],
  },
  {
    num: '05',
    icon: 'TagIcon',
    label: 'Offers, Discounts & Subscription Plans',
    desc: 'Run flash sales, cart-level discounts, buy-X-get-Y, and free delivery above a threshold. Weekly or monthly grocery subscription plans let customers lock in savings and guarantee recurring revenue for your store.',
    pills: ['Flash sales', 'Coupon codes', 'Weekly subscriptions', 'Free delivery threshold', 'Loyalty cashback'],
  },
  {
    num: '06',
    icon: 'ChartBarIcon',
    label: 'Sales Analytics & Demand Forecasting',
    desc: "Machine learning analyses your order history to forecast demand by product, category, and delivery zone — so you stock the right quantity at the right time. Live dashboards show revenue, AOV, best-sellers, and repeat-customer rate.",
    pills: ['ML demand forecast', 'Best-seller tracking', 'AOV & revenue trends', 'Zone-wise analytics', 'Reorder intelligence'],
  },
];

const deliverySlots = [
  { label: 'Express', time: '60–90 min', icon: 'BoltIcon', color: '#DC2626', bg: 'rgba(220,38,38,0.08)' },
  { label: 'Morning', time: '8 AM – 12 PM', icon: 'SunIcon', color: '#D97706', bg: 'rgba(217,119,6,0.08)' },
  { label: 'Afternoon', time: '12 PM – 4 PM', icon: 'ClockIcon', color: '#15803D', bg: 'rgba(21,128,61,0.08)' },
  { label: 'Evening', time: '4 PM – 8 PM', icon: 'MoonIcon', color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
  { label: 'Next Day', time: 'Choose any slot', icon: 'CalendarDaysIcon', color: '#0459F7', bg: 'rgba(4,89,247,0.08)' },
  { label: 'Scheduled', time: 'Up to 7 days ahead', icon: 'ArrowPathIcon', color: '#0891B2', bg: 'rgba(8,145,178,0.08)' },
];

const pickerSteps = [
  { icon: 'InboxIcon', title: 'Order Received', desc: 'New order appears on the Picker App with a full itemised list, priority slot, and packing deadline.' },
  { icon: 'ShoppingCartIcon', title: 'Items Picked', desc: 'Picker navigates aisle-by-aisle with barcode scan confirmation. Out-of-stock triggers auto-substitution.' },
  { icon: 'CubeIcon', title: 'Packed & Labelled', desc: 'Packed bags are weight-verified and labelled with order ID, slot time, and customer address QR code.' },
  { icon: 'TruckIcon', title: 'Handed to Rider', desc: 'Rider scans the bag, confirms handover on the Driver App, and sets off on the optimised delivery route.' },
];

const storeFeatures = [
  'Product Catalog & Category Builder',
  'Real-time Stock Dashboard',
  'Low-Stock Alerts & Reorder Prompts',
  'Bulk Product Import via CSV',
  'Price Management & Bulk Update',
  'Weight-based Product Handling',
  'Expiry Date & Batch Tracking',
  'Store Operating Hours Setup',
  'Delivery Zone Configuration',
  'Substitution Management',
  'Banner & Campaign Management',
  'Tax & Invoice Configuration',
];

const customerAppFeatures = [
  'Smart Search & Voice Input',
  'Filter by Brand, Price & Diet',
  'Barcode Scanner to Add Items',
  'Slot Selection at Checkout',
  'Live Order Status Tracking',
  'Recurring & Subscription Orders',
  'Reorder Favourites (One Tap)',
  'Wishlist & Save for Later',
  'Multiple Delivery Addresses',
  'Digital Bills & Order History',
  'Loyalty Points & Referral',
  'Multi-payment: UPI, Card, COD',
];

const driverFeatures = [
  'Daily Delivery Assignment List',
  'Optimised Multi-stop Navigation',
  'Slot-wise Order Queue',
  'Bag Scan at Store Handover',
  'Proof of Delivery (Photo / OTP)',
  'Contactless Drop-off Option',
  'Earnings & Payout Dashboard',
  'Customer Call Masking',
  'Failed Delivery & Re-attempt',
  'Performance Score & Badges',
  'Cold-chain Alert (temp tracking)',
  'Trip History & Route Replay',
];

const analyticsFeatures = [
  { icon: 'ChartBarIcon', label: 'Daily Revenue & Order Count', sub: 'Real-time sales with hourly breakdown' },
  { icon: 'TagIcon', label: 'Best-selling Products', sub: 'Top SKUs by volume and revenue' },
  { icon: 'ArrowPathIcon', label: 'Customer Repeat Rate', sub: 'Measure retention and LTV' },
  { icon: 'CubeIcon', label: 'Inventory Turnover Rate', sub: 'Identify slow-moving stock' },
  { icon: 'CalendarDaysIcon', label: 'Slot Utilisation Report', sub: 'Demand by time slot and zone' },
  { icon: 'BoltIcon', label: 'Demand Forecast by SKU', sub: 'ML prediction for next 7–30 days' },
];

const subscriptionBenefits = [
  { icon: 'CalendarDaysIcon', title: 'Weekly & Monthly Plans', desc: 'Customers lock in their regular grocery list and schedule. Auto-charged and auto-dispatched on the chosen day.' },
  { icon: 'CurrencyRupeeIcon', title: 'Subscriber Discounts', desc: 'Offer exclusive pricing and free delivery to subscribers — increasing order frequency and average cart value.' },
  { icon: 'BellAlertIcon', title: 'Smart Reminders', desc: 'Automated push and WhatsApp reminders before each scheduled delivery keep cancellations low.' },
  { icon: 'AdjustmentsHorizontalIcon', title: 'Pause & Modify Anytime', desc: 'Customers can pause, skip, or edit their subscription from the app without contacting support.' },
];

const additionalFeatures = [
  { icon: 'BuildingStorefrontIcon', label: 'Multi-store / Dark Store', sub: 'Manage multiple outlets from one panel' },
  { icon: 'ShieldCheckIcon', label: 'Substitution Management', sub: 'Auto-suggest alternatives for out-of-stock' },
  { icon: 'ChatBubbleLeftRightIcon', label: 'WhatsApp Order Booking', sub: 'Place orders via chatbot, no app needed' },
  { icon: 'DocumentTextIcon', label: 'Digital Invoicing & GST', sub: 'Auto-generated bills per order' },
  { icon: 'GlobeAltIcon', label: 'Zone-wise Delivery Rules', sub: 'Different fees and ETAs per zone' },
  { icon: 'StarIcon', label: 'Ratings & Reviews', sub: 'Per-product and per-order feedback' },
];

const stats = [
  { value: '12+', label: 'Grocery Category Types', icon: 'ShoppingCartIcon' },
  { value: 'Real-time', label: 'Inventory Sync', icon: 'CubeIcon' },
  { value: 'Slot-based', label: 'Delivery Scheduling', icon: 'CalendarDaysIcon' },
  { value: '21 Days', label: 'Go-Live Timeline', icon: 'BoltIcon' },
];

/* ─── COMPONENT ─────────────────────────────────────── */

export default function GroceryContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO (white + live store panel) ── */}
      <div ref={heroRef} className="bg-white relative overflow-hidden">
        {/* Subtle green grid */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(21,128,61,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(21,128,61,0.04) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
        {/* Green glow top-right */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(21,128,61,0.07) 0%, transparent 70%)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-14 items-center">

            {/* Left */}
            <div>
              <Link href="/products" className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-[#64748B] text-sm font-medium mb-7 hover:text-[#15803D] transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(21,128,61,0.35)] bg-[rgba(21,128,61,0.06)] text-[11px] font-semibold tracking-widest text-[#15803D] uppercase mb-5">
                <Icon name="ShoppingCartIcon" size={11} className="text-[#15803D]" />
                Grocery Platform
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-[#0F172A] leading-tight tracking-tight mb-5">
                Launch your branded
                <br />
                <span className="italic font-light" style={{ color: '#15803D' }}>grocery delivery</span>
                <br />
                platform.
              </h1>

              <p className="animate-on-scroll stagger-3 text-[#475569] text-lg leading-relaxed mb-9 max-w-lg">
                Real-time inventory, slot-based delivery, and seamless store-to-door fulfilment — everything a local grocery business needs to go digital and scale.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4 mb-8">
                <Link href="/demo-booking" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold text-white hover:opacity-90 shadow-lg transition-all hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #15803D, #16A34A)' }}>
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-[#475569] border border-[rgba(15,23,42,0.15)] hover:border-[#15803D] hover:text-[#15803D] transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2">
                {['21-day go-live', 'Real-time inventory', 'Slot-based delivery', 'ML demand forecast'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-[#64748B] font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#15803D]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — live store panel */}
            <div className="animate-on-scroll stagger-3">
              <div className="rounded-2xl overflow-hidden border border-[rgba(21,128,61,0.2)] shadow-lg">
                {/* Panel header */}
                <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #14532D, #15803D)' }}>
                  <div className="flex items-center gap-2">
                    <Icon name="BuildingStorefrontIcon" size={16} className="text-white" />
                    <span className="text-sm font-semibold text-white">Live Store Dashboard</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-[#4ADE80] opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4ADE80]" />
                    </span>
                    <span className="text-xs text-white/65 font-medium">Syncing</span>
                  </div>
                </div>

                {/* Category grid */}
                <div className="p-5 bg-white">
                  <p className="text-[10px] font-semibold text-[#64748B] uppercase tracking-widest mb-3">Categories in Your Store</p>
                  <div className="grid grid-cols-4 gap-2">
                    {groceryCategories.slice(0, 8).map((c) => (
                      <div key={c.label} className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-[rgba(21,128,61,0.1)] bg-[#F0FDF4] hover:border-[rgba(21,128,61,0.3)] transition-colors text-center group cursor-default">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[rgba(21,128,61,0.1)] group-hover:scale-110 transition-transform">
                          <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={15} className="text-[#15803D]" />
                        </div>
                        <span className="text-[9px] font-semibold text-[#0F172A] leading-tight">{c.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stock + slot status */}
                <div className="px-5 pb-5 grid grid-cols-2 gap-3">
                  <div className="rounded-xl p-3.5 border border-[rgba(21,128,61,0.15)] bg-[#F0FDF4]">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="CubeIcon" size={13} className="text-[#15803D]" />
                      <span className="text-[10px] font-semibold text-[#15803D] uppercase tracking-wider">Inventory</span>
                    </div>
                    <p className="text-sm font-semibold text-[#0F172A]">Real-time sync</p>
                    <p className="text-[10px] text-[#64748B]">Low-stock alerts active</p>
                  </div>
                  <div className="rounded-xl p-3.5 border border-[rgba(21,128,61,0.15)] bg-[#F0FDF4]">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="CalendarDaysIcon" size={13} className="text-[#15803D]" />
                      <span className="text-[10px] font-semibold text-[#15803D] uppercase tracking-wider">Delivery</span>
                    </div>
                    <p className="text-sm font-semibold text-[#0F172A]">6 slot options</p>
                    <p className="text-[10px] text-[#64748B]">Express to next-day</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div style={{ background: 'linear-gradient(135deg, #052E16 0%, #14532D 55%, #15803D 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-7`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/15">
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#4ADE80]" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-white leading-none mb-0.5">{s.value}</div>
                  <div className="text-xs text-white/55 font-medium">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CORE 6 FEATURES (full-width split rows) ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(21,128,61,0.3)] bg-[rgba(21,128,61,0.07)] text-[11px] font-semibold tracking-widest text-[#15803D] uppercase mb-4">
              Key Features
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Six capabilities that run your{' '}
              <span className="italic font-light text-[#15803D]">entire grocery business.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-2xl mx-auto">From inventory to doorstep — every stage of grocery delivery automated and optimised.</p>
          </div>

          <div className="flex flex-col divide-y divide-[rgba(21,128,61,0.1)] rounded-2xl overflow-hidden border border-[rgba(21,128,61,0.1)]">
            {coreFeatures.map((f, i) => (
              <div
                key={f.num}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group grid md:grid-cols-[1fr_1.4fr] gap-0 hover:bg-[rgba(21,128,61,0.02)] transition-colors`}
                style={{ background: i % 2 === 0 ? 'white' : '#F0FDF4' }}
              >
                {/* Left — number + icon + title + pills */}
                <div className="p-8 md:border-r border-[rgba(21,128,61,0.1)]">
                  <div className="flex items-center gap-4 mb-4">
                    <span
                      className="font-mono-custom text-xs font-bold w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #15803D, #16A34A)' }}
                    >
                      {f.num}
                    </span>
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      style={{ background: 'rgba(21,128,61,0.1)' }}
                    >
                      <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#15803D]" />
                    </div>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#0F172A] leading-snug mb-4">{f.label}</h3>
                  <div className="flex flex-wrap gap-2">
                    {f.pills.map((p) => (
                      <span key={p} className="text-[11px] font-semibold px-2.5 py-1 rounded-full text-[#15803D]" style={{ background: 'rgba(21,128,61,0.08)' }}>
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Right — description */}
                <div className="p-8 flex items-center">
                  <p className="text-[#475569] text-base leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── DELIVERY SLOTS ── */}
      <div className="py-24" style={{ background: '#F0FDF4' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(21,128,61,0.3)] bg-[rgba(21,128,61,0.07)] text-[11px] font-semibold tracking-widest text-[#15803D] uppercase mb-4">
              Delivery Slots
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Customers choose when.{' '}
              <span className="italic font-light text-[#15803D]">You decide how many.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Slot-based delivery keeps your operations predictable. Cap slot capacity to match driver availability and prevent overload.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {deliverySlots.map((s, i) => (
              <div
                key={s.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group flex items-center gap-5 p-6 rounded-2xl bg-white border hover:shadow-md transition-all duration-300`}
                style={{ borderColor: s.color + '22' }}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                  style={{ background: s.bg }}
                >
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={24} style={{ color: s.color }} />
                </div>
                <div>
                  <p className="font-display text-base font-semibold text-[#0F172A]">{s.label}</p>
                  <p className="text-sm text-[#64748B] mt-0.5">{s.time}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 grid md:grid-cols-3 gap-4">
            {[
              { icon: 'AdjustmentsHorizontalIcon', title: 'Slot Capacity Control', desc: 'Set a maximum order count per slot. Once full, the slot is hidden from customers automatically.' },
              { icon: 'BellAlertIcon', title: 'Rescheduling & Reminders', desc: 'Customers reschedule from the app. Auto-reminders sent before each delivery window.' },
              { icon: 'ChartBarIcon', title: 'Slot Utilisation Reports', desc: 'See which slots fill fastest and which zones are overloaded — and optimise delivery staffing accordingly.' },
            ].map((item) => (
              <div key={item.title} className="rounded-2xl p-6 bg-white border border-[rgba(21,128,61,0.12)] hover:border-[rgba(21,128,61,0.3)] hover:shadow-sm transition-all">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(21,128,61,0.1)' }}>
                  <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#15803D]" />
                </div>
                <p className="font-semibold text-[#0F172A] mb-2">{item.title}</p>
                <p className="text-sm text-[#475569] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PICKER WORKFLOW (dark green) ── */}
      <div className="py-24 relative overflow-hidden" style={{ background: '#052E16' }}>
        <div className="absolute inset-0 grid-lines opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[300px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(21,128,61,0.4) 0%, transparent 70%)', filter: 'blur(50px)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#4ADE80]/30 bg-[#4ADE80]/10 text-[11px] font-semibold tracking-widest text-[#4ADE80] uppercase mb-5">
              In-store Workflow
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight">
              From shelf to customer door —{' '}
              <span className="italic font-light text-[#4ADE80]">every step tracked.</span>
            </h2>
            <p className="text-white/50 text-lg mt-4 max-w-xl mx-auto">The Picker App guides your in-store staff through order fulfilment — from item selection to packed handover — with barcode confirmation at every step.</p>
          </div>

          {/* 4-step flow */}
          <div className="relative grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="hidden lg:block absolute top-[26px] left-[calc(12.5%+20px)] right-[calc(12.5%+20px)] h-px"
              style={{ background: 'linear-gradient(90deg, rgba(74,222,128,0.05), rgba(74,222,128,0.3) 20%, rgba(74,222,128,0.3) 80%, rgba(74,222,128,0.05))' }} />
            {pickerSteps.map((step, i) => (
              <div key={step.title} className={`animate-on-scroll stagger-${i + 2} flex flex-col items-center text-center`}>
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 z-10"
                  style={{ background: 'linear-gradient(135deg, #15803D, #16A34A)', boxShadow: '0 0 24px rgba(21,128,61,0.45)' }}
                >
                  <Icon name={step.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-white" />
                </div>
                <div className="text-[10px] font-bold text-[#4ADE80]/50 tracking-widest uppercase mb-1.5">Step {i + 1}</div>
                <h3 className="font-semibold text-white text-sm mb-2">{step.title}</h3>
                <p className="text-xs text-white/45 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-3 mt-14 flex flex-wrap justify-center gap-3">
            {['Barcode Scan Confirmation', 'Out-of-stock Substitution', 'Weight Verification', 'QR-coded Packaging', 'Rider Handover Scan'].map((p) => (
              <div key={p} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#4ADE80]/20 bg-white/[0.04]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80]" />
                <span className="text-sm text-white/60 font-medium">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 3 APPS ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(21,128,61,0.3)] bg-[rgba(21,128,61,0.07)] text-[11px] font-semibold tracking-widest text-[#15803D] uppercase mb-4">
              Platform Apps
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Store, Customer & Driver —{' '}
              <span className="italic font-light text-[#15803D]">all covered.</span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Store App */}
            <div className="animate-on-scroll stagger-2 rounded-2xl overflow-hidden border border-[rgba(21,128,61,0.15)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6" style={{ background: 'linear-gradient(135deg, #14532D, #15803D)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="BuildingStorefrontIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Store Dashboard</h3>
                    <p className="text-white/60 text-xs">Web + Picker App</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2" style={{ background: '#F0FDF4' }}>
                {storeFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-[rgba(21,128,61,0.08)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(21,128,61,0.12)' }}>
                      <Icon name="CheckIcon" size={9} className="text-[#15803D]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer App */}
            <div className="animate-on-scroll stagger-3 rounded-2xl overflow-hidden border border-[rgba(15,23,42,0.07)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6 bg-[#0F172A]">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                    <Icon name="DevicePhoneMobileIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Customer App</h3>
                    <p className="text-white/50 text-xs">iOS & Android</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2 bg-[#F5F5F5]">
                {customerAppFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-[rgba(15,23,42,0.06)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center bg-[#0F172A]">
                      <Icon name="CheckIcon" size={9} className="text-white" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Driver App */}
            <div className="animate-on-scroll stagger-4 rounded-2xl overflow-hidden border border-[rgba(21,128,61,0.15)] shadow-sm hover:shadow-md transition-shadow">
              <div className="px-7 py-6" style={{ background: 'linear-gradient(135deg, #166534, #15803D)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                    <Icon name="TruckIcon" size={22} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-semibold text-white">Driver App</h3>
                    <p className="text-white/60 text-xs">iOS & Android</p>
                  </div>
                </div>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-2" style={{ background: '#F0FDF4' }}>
                {driverFeatures.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-[rgba(21,128,61,0.08)]">
                    <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(21,128,61,0.15)' }}>
                      <Icon name="CheckIcon" size={9} className="text-[#15803D]" />
                    </div>
                    <span className="text-xs font-medium text-[#0F172A]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── SUBSCRIPTION PLANS ── */}
      <div className="py-24" style={{ background: '#F0FDF4' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(21,128,61,0.3)] bg-[rgba(21,128,61,0.07)] text-[11px] font-semibold tracking-widest text-[#15803D] uppercase mb-5">
                Subscriptions
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-5">
                Turn weekly shoppers into{' '}
                <span className="italic font-light text-[#15803D]">loyal subscribers.</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-8">
                Grocery subscription plans let customers lock in their regular basket and schedule — auto-charged, auto-dispatched, no effort on repeat. For your store, it means predictable daily revenue and better inventory planning.
              </p>
              <div className="flex flex-col gap-4">
                {subscriptionBenefits.map((b, i) => (
                  <div key={b.title} className={`animate-on-scroll stagger-${i + 2} flex gap-4 p-5 rounded-2xl bg-white border border-[rgba(21,128,61,0.1)] hover:border-[rgba(21,128,61,0.3)] transition-all group`}>
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'rgba(21,128,61,0.1)' }}>
                      <Icon name={b.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#15803D]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0F172A] mb-1">{b.title}</p>
                      <p className="text-sm text-[#475569] leading-relaxed">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — analytics panel */}
            <div className="animate-on-scroll stagger-2 flex flex-col gap-4">
              <div className="rounded-2xl p-7 border border-[rgba(21,128,61,0.15)] bg-white">
                <h3 className="font-display text-xl font-semibold text-[#0F172A] mb-6">Sales Analytics & Demand Forecasting</h3>
                <div className="grid grid-cols-2 gap-3">
                  {analyticsFeatures.map((a) => (
                    <div key={a.label} className="rounded-xl p-4 border border-[rgba(21,128,61,0.1)] hover:border-[rgba(21,128,61,0.3)] hover:bg-[#F0FDF4] transition-all group">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform" style={{ background: 'rgba(21,128,61,0.1)' }}>
                        <Icon name={a.icon as Parameters<typeof Icon>[0]['name']} size={17} className="text-[#15803D]" />
                      </div>
                      <p className="font-semibold text-xs text-[#0F172A] mb-1">{a.label}</p>
                      <p className="text-[10px] text-[#64748B] leading-relaxed">{a.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl p-6 border border-[rgba(21,128,61,0.2)] text-center" style={{ background: 'linear-gradient(135deg, rgba(21,128,61,0.06), rgba(22,163,74,0.03))' }}>
                <p className="font-semibold text-[#15803D]">ML demand forecasting predicts next 7–30 day order volumes by SKU, zone, and slot — so you never over-stock or run out of fast-moving items.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── ADDITIONAL FEATURES ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(21,128,61,0.3)] bg-[rgba(21,128,61,0.07)] text-[11px] font-semibold tracking-widest text-[#15803D] uppercase mb-4">
              More Capabilities
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Everything else your{' '}
              <span className="italic font-light text-[#15803D]">grocery business needs.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {additionalFeatures.map((f, i) => (
              <div
                key={f.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} flex items-center gap-4 p-6 rounded-2xl border border-[rgba(21,128,61,0.1)] bg-[#F0FDF4] hover:border-[rgba(21,128,61,0.3)] hover:bg-white hover:shadow-md transition-all duration-300 group`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'rgba(21,128,61,0.1)' }}>
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#15803D]" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-[#0F172A]">{f.label}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {['White-label Ready', 'Real-time Inventory', 'Slot-based Delivery', 'ML Demand Forecast'].map((item) => (
              <div key={item} className="flex items-center gap-2 p-3 bg-[#F0FDF4] rounded-xl border border-[rgba(21,128,61,0.12)]">
                <Icon name="CheckBadgeIcon" size={16} className="text-[#15803D] flex-shrink-0" />
                <span className="text-xs font-medium text-[#475569]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-24" style={{ background: '#F0FDF4' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #052E16 0%, #14532D 40%, #15803D 75%, #16A34A 100%)' }}
          >
            <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
            <div className="absolute top-0 right-0 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(74,222,128,0.15) 0%, transparent 70%)', transform: 'translate(50px,-50px)' }} />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#4ADE80]/30 bg-[#4ADE80]/10 text-[11px] font-semibold tracking-widest text-[#4ADE80] uppercase mb-6">
              <span className="relative flex h-1.5 w-1.5">
                <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-[#4ADE80] opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#4ADE80]" />
              </span>
              Ready to Launch?
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              Your branded grocery platform,{' '}
              <span className="italic font-light text-[#4ADE80]">live in 21 days.</span>
            </h2>

            <p className="relative text-white/60 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/65 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={14} />
                +91 9600830488
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/65 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={14} />
                contact@movio.in
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2 text-white/65 font-medium">
                <Icon name="GlobeAltIcon" size={14} />
                www.movio.in
              </span>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold bg-white text-[#15803D] hover:bg-white/90 shadow-lg transition-all duration-200 hover:-translate-y-0.5"
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/30 hover:border-white/60 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
